'use client';
import type { FC } from 'react';
import type { PromotionTallBannerWidgetData } from '@weberus/shared-types';
import type { Swiper as SwiperCore } from 'swiper';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { SubHeader } from '@yanolja-rnd/nol-design-system';
import { useRef, useState } from 'react';
import { cn } from '@/app/_shared/utils/tw-cn';
import { SwiperLayer } from './swiper-layer';

export interface PromotionTallBannerProps {
  data: PromotionTallBannerWidgetData;
  pageNameLog: HomeLogPageNameValue;
}

const MINIMUM_ITEM_COUNT = 4;
/**
 * 프로모션 배너 세로형
 * @see {@link https://www.figma.com/design/zlHuiOHmYv1ghVDbTHUSvW/%5BGUIDE%5D-%EC%9C%84%EC%A0%AF%2F%EC%98%A4%EB%8D%94%EB%A7%81?node-id=262-32568&m=dev}
 */
export const PromotionTallBanner: FC<PromotionTallBannerProps> = ({ data, pageNameLog }) => {
  const { header, items, logMeta } = data;
  const swiperRef = useRef<SwiperCore | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const currentIdx = (selectedIndex + 1).toString().padStart(2, '0');
  const totalIdx = items.length.toString().padStart(2, '0');

  // 상품이 최소 갯수를 만족하는 경우에만 렌더링
  if (!items || items.length < MINIMUM_ITEM_COUNT) {
    return null;
  }

  return (
    <div className="relative h-full w-full">
      {/* mobile 에서 보여질 background image */}
      <div
        className="absolute h-full w-full justify-center"
        style={{ backgroundImage: `url(${items[selectedIndex].image.url})` }}>
        <div className="h-full w-full bg-colors-black bg-opacity-60 backdrop-blur-[40px] pc:bg-colors-white pc:bg-opacity-80 pc:backdrop-blur-[100px]" />
      </div>
      <div
        className={cn('relative mb-32 flex w-full flex-col items-center justify-center pt-32 pc:mb-40 pc:pt-40', {
          'pc:pt-0': !!header,
        })}>
        {!!header && (
          <>
            <div
              className={cn('text-white mb-16 flex flex-col items-center justify-center pc:hidden', {
                'h-[5rem]': !header.subTitle,
                'h-[6.8rem]': !!header.subTitle,
              })}>
              <div className="text-ellipsis--2 text-center text-static-white typography-subtitle-18-bold">
                {header.title}
              </div>
              {!!header.subTitle && (
                <div className="mt-2 text-static-white typography-body-13-regular">{header.subTitle}</div>
              )}
            </div>
            {/* pc만 노출 */}
            <div className="mt-20 hidden pc:block pc:w-pc-size">
              <SubHeader className="px-16" title={header.title} subTitle={header.subTitle} theme="ghost" />
            </div>
          </>
        )}
        <SwiperLayer
          pageNameLog={pageNameLog}
          widgetLogMeta={logMeta}
          items={items}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          swiperRef={swiperRef}
          showNavButton={items.length > MINIMUM_ITEM_COUNT}
        />
        <div className="mt-16 flex flex-row items-center justify-center text-static-white pc:hidden">
          <span className="mr-5 typography-body-12-bold">{currentIdx}</span>
          <span className="typography-body-12-regular">{`/ ${totalIdx}`}</span>
        </div>
      </div>
    </div>
  );
};
